/**
 * The map picker in the Sconce (2026-09-01).
 *
 * User: "how do I select a specific map once I've bought a contract? There
 * needs to be a selection option, as well as a visual preview of these
 * contracts, a description, and the difficulty level."
 *
 * The Contracts panel used to be two lines of text and a Buy button. You
 * were asked to spend Wax on a floor you had never seen, described only by
 * its own name ("Start your next run in OSSUARY"), with no way to say which
 * one you actually wanted to run next — the deepest contract you happened to
 * own was spent automatically on whatever run came next.
 *
 * This is the whole choice on one screen: every floor including the free
 * one, what it looks like, what is down there, and one control that says
 * where you are going.
 *
 * String builders rather than DOM builders because the hub renders its
 * panels with `innerHTML`, and pure so the interesting states — owned but
 * exhausted, owned and selected, not owned at all — can be tested without a
 * hub, a server, or a purchase.
 *
 * Tailwind v4 extracts class names statically, so every class here is a
 * literal. A template hole inside a class attribute produces no CSS rule at
 * all (learned the hard way on the HUD pass), which is why the difficulty
 * pips and tints below are written out per branch instead of interpolated.
 */

import type { ContractDefRow, ContractRow, MapOptionRow, RunModifierRow } from '../net/contracts-api';
import { amountMarkup } from './currency';
import { iconMarkup } from './icon';
import { t, tp, N_ } from '../i18n/i18n';
import { serverText } from '../config/served-text';

/** Escapes catalog copy before it lands in markup. Server-authored today, still not worth trusting into innerHTML. */
function esc(text: string): string {
  return text.replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string
  ));
}

/**
 * Difficulty as filled pips out of three.
 *
 * Three floors, three pips — the scale is the game's actual depth range, not
 * an invented 1-10 that would leave seven levels permanently unused.
 */
export function difficultyPips(depth: number): string {
  const filled = Math.max(0, Math.min(3, Math.round(depth)));
  const pips: string[] = [];
  for (let i = 0; i < 3; i++) {
    pips.push(i < filled
      ? '<span class="w-2 h-2 rounded-full bg-gold border border-black/40"></span>'
      : '<span class="w-2 h-2 rounded-full bg-transparent border border-gold/40"></span>');
  }
  return `<span class="flex items-center gap-1" aria-label="${esc(t('Difficulty {n} of 3', { n: filled }))}">${pips.join('')}</span>`;
}

/** "3 runs left" / "1 run left" — charges are the thing a player is tracking, not the purchase date. */
export function usesLabel(usesRemaining: number, usesTotal: number): string {
  const left = Math.max(0, Math.min(usesRemaining, usesTotal));
  // A one-run contract has nothing to count down, so it says what it is rather
  // than "1 of 1 runs left" — which reads like a charge meter on a thing that
  // has no meter (2026-09-07).
  if (usesTotal === 1) return left > 0 ? t('Ready \u2014 one run') : t('Spent');
  return tp(usesTotal, '{left} of {n} run left', '{left} of {n} runs left', { left });
}

/**
 * The contract this player can actually spend on a given card: AVAILABLE,
 * with charges left. An exhausted one is history, not inventory.
 *
 * Matched on `contractDefId` since 2026-09-04, not on stratum — a floor sells
 * several different cards now, and owning a Bone Tithe must not make the
 * plain Ossuary Entry beside it read as owned.
 */
export function usableContractFor(defId: string, contracts: readonly ContractRow[]): ContractRow | null {
  return contracts.find((c) =>
    c.contractDefId === defId && c.status === 'AVAILABLE' && c.usesRemaining > 0) ?? null;
}

/** Every still-spendable contract for any card on this floor. */
export function usableContractsOn(option: MapOptionRow, contracts: readonly ContractRow[]): ContractRow[] {
  const ids = new Set(option.contracts.map((c) => c.id));
  return contracts.filter((c) => ids.has(c.contractDefId) && c.status === 'AVAILABLE' && c.usesRemaining > 0);
}

export interface MapCardState {
  /** The contract currently spending its charges on the next run, if any. */
  selectedContractId: string | null;
  /** Every contract this player owns, in any state. */
  contracts: readonly ContractRow[];
}

/**
 * What a modifier actually does, in the same derived-not-written spirit as
 * `stratum-info.ts`'s threat lines: read off the numbers rather than
 * hand-written per card, so a re-tune can never leave a card describing
 * itself wrongly.
 *
 * Percentage *points*, not a percentage of a percentage — "+20% rare chests"
 * on a floor that already rolls 10% is 30%, and writing it as a multiplier
 * would be the more precise lie.
 */
export function modifierLines(modifier: RunModifierRow): string[] {
  const lines: string[] = [];
  if (modifier.rareChanceBonus > 0) lines.push(t('+{n}% rare chests', { n: Math.round(modifier.rareChanceBonus * 100) }));
  if (modifier.bonewrightMultiplier > 1) {
    const pct = Math.round((modifier.bonewrightMultiplier - 1) * 100);
    lines.push(t('+{n}% Bonewrights', { n: pct }));
  }
  if (modifier.swarmSizeBonus > 0) lines.push(t('Swarms +{n}', { n: modifier.swarmSizeBonus }));
  return lines;
}

/**
 * What a price buys, said next to the price.
 *
 * Contracts were single-run for part of 2026-09-07 (user: *"You just pay for 1
 * run and then reset"*) and became four-run packs the same day with the x4
 * price pass — a x4 single-run card would have cost more Wax than the floor it
 * unlocks returns.
 *
 * Both branches are live and neither is speculative now. The singular still
 * reads "One run" rather than "1 run", because a lone numeral beside a price
 * invites being read as part of it; that was the reason then and it is the
 * reason a future one-run card would want it again.
 */
export function chargesLabel(uses: number): string {
  return uses === 1 ? t('One run') : tp(uses, '{n} run', '{n} runs');
}

/**
 * One purchasable card inside a floor.
 *
 * Three states, and each says a different thing:
 * - **selected** — this is what the next run spends. Nothing to press.
 * - **owned** — press to make it the next run, and it says how many are left.
 * - **not owned** — press to buy, priced with its charge count beside it so
 *   the number is never mistaken for a per-run price.
 */
export function contractCardMarkup(def: ContractDefRow, state: MapCardState): string {
  // The selected copy first: holding two of the same card made this pick
  // whichever came first in the ledger, so the floor read "Selected" while
  // its own card offered "Run this" (2026-10-09).
  const owned = state.contracts.find((c) => c.id === state.selectedContractId && c.contractDefId === def.id
      && c.status === 'AVAILABLE' && c.usesRemaining > 0)
    ?? usableContractFor(def.id, state.contracts);
  const selected = !!owned && owned.id === state.selectedContractId;
  const ring = selected ? 'border-gold/70 bg-gold/5' : 'border-border';
  const effects = modifierLines(def.modifier)
    .map((line) => `<span class="chip chip-gold">${esc(line)}</span>`)
    .join('');

  let action: string;
  if (selected) {
    action = `<span class="btn btn-stone-on shrink-0">${iconMarkup('check', 15)}${esc(t('Selected'))}</span>`;
  } else if (owned) {
    action = `<button data-select-contract="${esc(owned.id)}" class="btn btn-stone shrink-0">${esc(t('Run this'))}</button>`;
  } else {
    action = `<button data-buy-contract="${esc(def.id)}" class="btn btn-stone shrink-0">`
      + `<span class="text-text-dim">${esc(t('Buy'))}</span>${amountMarkup('wax', def.priceWax, { hideLabel: true })}`
      + `</button>`;
  }

  const status = owned
    ? `<span class="text-[11px] text-gold">${usesLabel(owned.usesRemaining, owned.usesTotal)}</span>`
    : `<span class="text-[11px] text-text-dim">${esc(t('{uses}, then it is spent', { uses: chargesLabel(def.uses) }))}</span>`;

  return `
    <div data-contract-card="${esc(def.id)}" class="rounded-lg border ${ring} p-2.5 flex flex-col gap-1.5">
      <div class="flex items-start justify-between gap-2">
        <div class="min-w-0">
          <p class="font-heading text-[13px] text-text leading-tight">${esc(t(def.name))}</p>
          <p class="text-[12px] text-text-dim leading-snug">${esc(t(def.blurb))}</p>
        </div>
        ${action}
      </div>
      ${effects ? `<div class="flex flex-wrap gap-1.5">${effects}</div>` : ''}
      ${status}
    </div>
  `;
}

/**
 * The free run, drawn as a card so the Cellars' two options sit side by side.
 *
 * Selecting it is how you *stop* spending a contract — the escape hatch the
 * old automatic-deepest-wins behaviour never had, and now also the way to
 * take the Cellars plain when you are holding a Scavenger's Writ.
 */
function freeEntryMarkup(state: MapCardState): string {
  const selected = state.selectedContractId === null;
  const ring = selected ? 'border-gold/70 bg-gold/5' : 'border-border';
  const action = selected
    ? `<span class="btn btn-stone-on shrink-0">${iconMarkup('check', 15)}${esc(t('Selected'))}</span>`
    : `<button data-select-contract="" class="btn btn-stone shrink-0">${esc(t('Run this'))}</button>`;
  return `
    <div data-contract-card="" class="rounded-lg border ${ring} p-2.5 flex flex-col gap-1.5">
      <div class="flex items-start justify-between gap-2">
        <div class="min-w-0">
          <p class="font-heading text-[13px] text-text leading-tight">${esc(t('Plain Delve'))}</p>
          <p class="text-[12px] text-text-dim leading-snug">${esc(t('No contract, no cost. The floor as everyone finds it.'))}</p>
        </div>
        ${action}
      </div>
      <span class="text-[11px] text-text-dim">${esc(t('Free — always available'))}</span>
    </div>
  `;
}

/** One floor: what it looks like, what is down there, and every way to buy in. */
export function mapCardMarkup(option: MapOptionRow, state: MapCardState): string {
  const { stratum } = option;
  const ownedHere = usableContractsOn(option, state.contracts);
  const activeHere = (option.freeEntry && state.selectedContractId === null)
    || ownedHere.some((c) => c.id === state.selectedContractId);
  const ring = activeHere ? 'border-gold' : 'border-border';
  const threats = stratum.threats
    .map((tag) => `<span class="chip">${esc(serverText(tag))}</span>`)
    .join('');
  const cards = [
    option.freeEntry ? freeEntryMarkup(state) : '',
    ...option.contracts.map((def) => contractCardMarkup(def, state)),
  ].join('');

  return `
    <div data-map-card="${esc(stratum.id)}" class="frame-inset overflow-hidden ${ring}">
      <div class="relative aspect-[2/1] bg-bg">
        <img src="${esc(stratum.preview)}" alt="${esc(t(stratum.name))}" loading="lazy"
             class="absolute inset-0 w-full h-full object-cover" />
        <div class="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/85 to-transparent"></div>
        <div class="absolute inset-x-0 bottom-0 p-2.5 flex items-end justify-between gap-2">
          <p class="font-heading text-sm text-text drop-shadow">${esc(t(stratum.name))}</p>
          ${difficultyPips(stratum.depth)}
        </div>
      </div>
      <div class="p-3 flex flex-col gap-2">
        <p class="text-[13px] text-text-dim leading-snug">${esc(t(stratum.description))}</p>
        <div class="flex flex-wrap gap-1.5">${threats}</div>
        <div class="flex flex-col gap-2">${cards}</div>
      </div>
    </div>
  `;
}

/**
 * The floor, as a tile (2026-10-09).
 *
 * User: *"I really don't like the Contracts pop-up window. It's overloaded
 * with text, which makes you scroll… keep the contract visual and some tags.
 * Then, after clicking, the user can view more detailed information."*
 *
 * So the catalog shows only what a glance needs — the floor's art, its
 * difficulty, one badge saying where you stand with it, and two tags — and
 * the whole tile is the button into `mapDetailMarkup`. Nothing here buys or
 * selects; every decision happens one tap deeper, where there is room to read
 * what it costs.
 */
export function mapTileStatus(option: MapOptionRow, state: MapCardState): { text: string; tone: 'gold' | 'owned' | 'dim' } {
  const ownedHere = usableContractsOn(option, state.contracts);
  const activeHere = (option.freeEntry && state.selectedContractId === null)
    || ownedHere.some((c) => c.id === state.selectedContractId);
  if (activeHere) return { text: t('Selected'), tone: 'gold' };
  if (ownedHere.length > 0) return { text: tp(ownedHere.length, '{n} owned', '{n} owned'), tone: 'owned' };
  if (option.freeEntry) return { text: t('Free'), tone: 'owned' };
  const cheapest = Math.min(...option.contracts.map((c) => c.priceWax));
  return Number.isFinite(cheapest)
    ? { text: t('From {n} Wax', { n: cheapest }), tone: 'dim' }
    : { text: t('Closed'), tone: 'dim' };
}

export function mapTileMarkup(option: MapOptionRow, state: MapCardState): string {
  const { stratum } = option;
  const status = mapTileStatus(option, state);
  const active = status.tone === 'gold';
  const badge = {
    gold: 'bg-gold text-bg border-gold',
    owned: 'bg-black/55 text-gold-bright border-gold/50',
    dim: 'bg-black/55 text-text border-border',
  }[status.tone];
  const tags = stratum.threats.slice(0, 2)
    .map((tag) => `<span class="chip text-[11px]">${esc(serverText(tag))}</span>`).join('');
  const count = option.contracts.length + (option.freeEntry ? 1 : 0);
  return `
    <button type="button" data-open-map="${esc(stratum.id)}"
      class="map-tile group frame-inset overflow-hidden text-left flex flex-row sm:flex-col ${active ? 'border-gold' : ''}">
      <span class="relative block w-[44%] shrink-0 sm:w-auto aspect-[4/3] sm:aspect-[16/11] bg-bg overflow-hidden">
        <img src="${esc(stratum.preview)}" alt="" loading="lazy" draggable="false"
             class="map-tile-art absolute inset-0 w-full h-full object-cover" />
        <span class="absolute inset-x-0 bottom-0 h-3/4 bg-gradient-to-t from-black/90 via-black/40 to-transparent"></span>
        <span class="absolute top-2 left-2 text-[11px] font-semibold tracking-wide px-2 py-0.5 rounded-sm border ${badge}">${esc(status.text)}</span>
        <span class="absolute inset-x-0 bottom-0 p-2.5 flex items-end justify-between gap-2">
          <span class="font-heading text-[15px] text-text drop-shadow leading-tight">${esc(t(stratum.name))}</span>
          ${difficultyPips(stratum.depth)}
        </span>
      </span>
      <span class="p-2.5 flex flex-col gap-2 flex-1">
        <span class="flex flex-wrap gap-1.5">${tags}</span>
        <span class="mt-auto flex items-center justify-between text-[12px] text-text-dim">
          <span>${esc(tp(count, '{n} way in', '{n} ways in'))}</span>
          <span class="map-tile-more text-gold">${esc(t('View contracts'))} &rsaquo;</span>
        </span>
      </span>
    </button>
  `;
}

/**
 * One floor, opened: the full description and tags, every way in with its
 * Buy / Run this action, and how contracts work — the reading the tile left
 * out. The back button returns to the tiles; nothing else is lost.
 */
export function mapDetailMarkup(option: MapOptionRow, state: MapCardState): string {
  const { stratum } = option;
  const threats = stratum.threats.map((tag) => `<span class="chip">${esc(serverText(tag))}</span>`).join('');
  const cards = [
    option.freeEntry ? freeEntryMarkup(state) : '',
    ...option.contracts.map((def) => contractCardMarkup(def, state)),
  ].join('');
  return `
    <div data-map-detail="${esc(stratum.id)}" class="map-detail flex flex-col gap-3">
      <button type="button" data-close-map class="btn btn-quiet self-start px-3 gap-1.5"><span aria-hidden="true" class="text-gold text-base leading-none">&lsaquo;</span>${esc(t('All floors'))}</button>
      <div class="frame-inset overflow-hidden">
        <div class="relative aspect-[3/1] sm:aspect-[4/1] bg-bg">
          <img src="${esc(stratum.preview)}" alt="${esc(t(stratum.name))}" class="absolute inset-0 w-full h-full object-cover" />
          <div class="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/85 to-transparent"></div>
          <div class="absolute inset-x-0 bottom-0 p-3 flex items-end justify-between gap-2">
            <p class="font-heading text-lg text-text drop-shadow">${esc(t(stratum.name))}</p>
            ${difficultyPips(stratum.depth)}
          </div>
        </div>
        <div class="p-3 flex flex-col gap-2">
          <p class="text-[13px] text-text-dim leading-snug">${esc(t(stratum.description))}</p>
          <div class="flex flex-wrap gap-1.5">${threats}</div>
        </div>
      </div>
      <p class="eyebrow">${esc(t('Ways in'))}</p>
      <div class="stagger grid grid-cols-1 sm:grid-cols-2 gap-2">${cards}</div>
      <details class="map-howto frame-inset px-3 py-2 text-[12px] text-text-dim">
        <summary class="cursor-pointer min-h-11 flex items-center font-heading text-[13px] text-gold">${esc(t('How contracts work'))}</summary>
        <ul class="list-disc pl-4 pb-2 flex flex-col gap-1 leading-snug">
          <li>${esc(t('A contract is bought once with Wax and is good for a set number of runs.'))}</li>
          <li>${t('The one marked {selected} is spent when your next Delve starts. Plain Delve spends nothing.', { selected: `<b class="text-text">${esc(t('Selected'))}</b>` })}</li>
          <li>${esc(t('Deeper floors pay more experience and richer chests, and the dead down there fight harder.'))}</li>
          <li>${esc(t('Owning a contract does not lock you in — switch back to Plain Delve any time.'))}</li>
        </ul>
      </details>
    </div>
  `;
}

/**
 * The line beside the queue button. This is the only place outside the panel
 * that says where you are about to go, and it exists because the panel is
 * behind a tile — without it, "Enter the Dungeon" is the same button whether
 * it is about to spend 1500 Wax or nothing.
 */
export function destinationLabel(
  catalog: readonly MapOptionRow[],
  contracts: readonly ContractRow[],
  selectedContractId: string | null,
): string {
  const selected = selectedContractId
    ? contracts.find((c) => c.id === selectedContractId && c.status === 'AVAILABLE' && c.usesRemaining > 0)
    : null;
  if (!selected) {
    const free = catalog.find((o) => o.freeEntry);
    return free ? t(free.stratum.name) : t('The Cellars');
  }
  const option = catalog.find((o) => o.stratum.id === selected.targetStratum);
  const name = option ? t(option.stratum.name) : selected.targetStratum;
  // The card's own name, not just the floor's: "The Ossuary" is three
  // different runs now, and this line is the only place outside the panel
  // that says which one the Play button is about to start.
  const def = option?.contracts.find((c) => c.id === selected.contractDefId);
  const card = def ? ` \u00b7 ${t(def.name)}` : '';
  return `${name}${card} \u00b7 ${usesLabel(selected.usesRemaining, selected.usesTotal)}`;
}


/**
 * A server refusal, in English (2026-09-07).
 *
 * The API answers with machine codes, which is right for an API and useless on
 * a card: a player who presses Buy and reads `contract_exhausted` has been
 * told nothing they can act on. That was half of why a working button was
 * reported as doing nothing — the other half was where the message appeared.
 *
 * Only codes a player can actually provoke are listed. Anything else falls
 * through to the raw message rather than being swallowed into a vague
 * "something went wrong", which would hide a real bug.
 */
export const CONTRACT_ERRORS: Readonly<Record<string, string>> = {
  contract_exhausted: N_('That contract has no runs left on it.'),
  contract_not_found: N_('That contract is no longer in your ledger.'),
  insufficient_funds: N_('Not enough Wax for that contract.'),
};

export function contractErrorMessage(code: string | undefined, fallback: string): string {
  const known = code ? CONTRACT_ERRORS[code] : undefined;
  return known ? t(known) : fallback;
}
